import { useMemo, useState } from 'react'

import { loadCommandDeckCockpitPrefs, type CommandDeckCockpitPrefs } from './lib/commandDeckCockpitPrefs'
import { Link, useNavigate } from 'react-router-dom'

import { COMMAND_DECK_PREVIEW_ROUTE_PATH } from './config/commandDeckMvp'
import { CommandDeckLiveBanner } from './components/CommandDeckLiveBanner'
import { PreviewBanner } from './components/PreviewBanner'
import { CommandDeckActivityRail } from './missionControl/CommandDeckActivityRail'
import { CommandDeckAiAdvisor } from './missionControl/CommandDeckAiAdvisor'
import { CommandDeckIdentityHub } from './missionControl/CommandDeckIdentityHub'
import { CommandDeckMissionControl } from './missionControl/CommandDeckMissionControl'
import { CommandDeckSettingsDrawer } from './missionControl/CommandDeckSettingsDrawer'
import { CommandDeckSurfaceConstellation } from './missionControl/CommandDeckSurfaceConstellation'
import { listAccessProfileIds } from './lib/getCommandDeckPreviewBundle'
import { buildCommandDeckWitnessFeed } from './lib/buildCommandDeckWitnessFeed'
import { resolveCommandDeckContinue } from './lib/resolveCommandDeckContinue'
import type {
  AccessProfileId,
  CommandDeckPreviewBundle,
  CommandDeckSurfaceMode,
  ToolCardModel,
} from './contracts/types'
import './commandDeck.css'
import './commandDeckMissionControl.css'

const PREVIEW_PROFILE_LABELS: Record<AccessProfileId, string> = {
  visitor: 'Visitor',
  creator_beta: 'Creator Beta (ORBIS)',
  revoked: 'Revoked',
}

export interface CommandDeckShellProps {
  mode: CommandDeckSurfaceMode
  bundle: CommandDeckPreviewBundle
  /** When false on live surface, show hold panel and hide interior mission sections. */
  unlocked: boolean
}

export function CommandDeckShell({ mode, bundle, unlocked }: CommandDeckShellProps) {
  const navigate = useNavigate()
  const isPreview = mode === 'preview'
  const showInteriorSections = isPreview
    ? bundle.accessProfileId === 'creator_beta'
    : unlocked
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [cockpitPrefs, setCockpitPrefs] = useState<CommandDeckCockpitPrefs>(() =>
    loadCommandDeckCockpitPrefs()
  )

  const continueResolution = useMemo(
    () =>
      resolveCommandDeckContinue({
        mode,
        unlocked: showInteriorSections,
        accessState: bundle.access.accessState,
        primary: bundle.bestNextMove.primary,
        lastActiveProjectId: bundle.access.lastActiveProjectId,
      }),
    [mode, showInteriorSections, bundle.access, bundle.bestNextMove.primary]
  )

  const witnessFeed = useMemo(
    () =>
      buildCommandDeckWitnessFeed({
        bundle,
        continueResolution,
        unlocked: showInteriorSections,
        mode,
      }),
    [bundle, continueResolution, showInteriorSections, mode]
  )

  const handlePrimaryCta = () => {
    if (!continueResolution.navigable || !continueResolution.targetPath) {
      return
    }
    navigate(continueResolution.targetPath)
  }

  const handleToolAction = useMemo(
    () => (tool: ToolCardModel) => {
      if (tool.toolId === 'guided_studio' && continueResolution.navigable) {
        const gsPath =
          continueResolution.targetPath?.startsWith('/guided-studio') ?
            continueResolution.targetPath
          : '/guided-studio'
        navigate(gsPath)
      }
    },
    [continueResolution.navigable, continueResolution.targetPath, navigate]
  )

  const deckClassName = [
    'command-deck',
    'command-deck--mission-control',
    'command-deck--cockpit-dark',
    cockpitPrefs.density === 'compact' ? 'command-deck--density-compact' : '',
    cockpitPrefs.motion === 'reduce' ? 'command-deck--motion-reduce' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={deckClassName}>
      <div className="command-deck__inner cd-mc-layout">
        {isPreview ? <PreviewBanner /> : <CommandDeckLiveBanner />}

        {isPreview ? (
          <nav className="command-deck-profile-switch" aria-label="Preview access profiles">
            Preview profile:{' '}
            {listAccessProfileIds().map((id) => (
              <Link
                key={id}
                to={`${COMMAND_DECK_PREVIEW_ROUTE_PATH}?accessProfile=${id}`}
                aria-current={bundle.accessProfileId === id ? 'page' : undefined}
              >
                {PREVIEW_PROFILE_LABELS[id]}
              </Link>
            ))}
          </nav>
        ) : null}

        <CommandDeckIdentityHub
          bundle={bundle}
          mode={mode}
          onOpenSettings={() => setSettingsOpen(true)}
        />

        {showInteriorSections ? (
          <div className="cd-mc-stage">
            <CommandDeckMissionControl
              bundle={bundle}
              unlocked={showInteriorSections}
              mode={mode}
              continueResolution={continueResolution}
              onPrimaryCta={handlePrimaryCta}
            />
            <CommandDeckActivityRail witnessFeed={witnessFeed} />
            <div className="cd-mc-stage__footer">
              <CommandDeckSurfaceConstellation
                bundle={bundle}
                compact
                onToolAction={handleToolAction}
              />
              <CommandDeckAiAdvisor bundle={bundle} />
            </div>
          </div>
        ) : (
          <CommandDeckMissionControl
            bundle={bundle}
            unlocked={false}
            mode={mode}
            continueResolution={continueResolution}
            onPrimaryCta={handlePrimaryCta}
          />
        )}
      </div>

      <CommandDeckSettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        bundle={bundle}
        mode={mode}
        onPrefsChange={setCockpitPrefs}
      />
    </div>
  )
}
